import type { QueryClient } from "@tanstack/react-query";
import type { AuthMeResponse } from "@/lib/api/client";

/**
 * Write a change to the tenant into the cached `POST /auth/me` profile,
 * rather than refetching it.
 *
 * A refetch flips `isProvisioning`, and `OnboardingLayout` swaps its step out
 * for a loader while that is in flight; leaving the cache alone leaves it
 * stale for its five-minute `staleTime`. Any refetch already in flight (the
 * client refetches on window focus) is cancelled first, because React Query
 * lets a fetch that resolves after this write overwrite it and restore the
 * profile the write exists to replace.
 *
 * `update` gets the cached tenant and returns the one to store; returning it
 * unchanged writes nothing new.
 */
export async function patchCachedTenant(
  queryClient: QueryClient,
  update: (tenant: AuthMeResponse["tenant"]) => AuthMeResponse["tenant"],
): Promise<void> {
  await queryClient.cancelQueries({ queryKey: ["auth", "me"] });
  queryClient.setQueriesData<AuthMeResponse>(
    { queryKey: ["auth", "me"] },
    (cached) =>
      cached ? { ...cached, tenant: update(cached.tenant) } : cached,
  );
}
